import {create} from 'zustand';
import {immer} from 'zustand/middleware/immer';
import {NodeId} from './networkStore';

export type EditMode = 'none' | 'addLink' | 'addPlayer' | 'dragNode';

export interface NodePopupLocation {
    mantine?: NodeId;
    pixi?: NodeId;
}


export interface NetworkEditState {
    activeEditMode: EditMode;
    selectedNodes: NodeId[];
    showConsentModal: boolean;
    nodePopupLocation: NodePopupLocation;

    /* ------------------- actions -------------------------------- */
    setActiveEditMode: (mode: EditMode) => void;
    setNodePopupLocation: (updater: (prev: NodePopupLocation) => NodePopupLocation) => void;
    selectNode: (nodeId: NodeId) => void;
    clearActiveEdit: () => void;
}

export const networkEditStore = create<NetworkEditState>()(
    immer((set) => ({
        activeEditMode: 'none',
        selectedNodes: [],
        showConsentModal: false,
        nodePopupLocation: {},

        setActiveEditMode: (mode) => {
            set((state) => {
                state.activeEditMode = mode;
                // when we leave an exclusive mode we also clear any transient UI
                if (mode === 'none') {
                    state.selectedNodes = [];
                    state.showConsentModal = false;
                }
            });
        },

        setNodePopupLocation: (updater) => {
            set((state) => {
                state.nodePopupLocation = updater(state.nodePopupLocation);
            });
        },

        selectNode: (nodeId) => {
            set((state) => {
                if (state.activeEditMode !== 'addLink') return;
                if (state.selectedNodes.includes(nodeId)) return;
                if (state.selectedNodes.length <= 1) state.selectedNodes.push(nodeId);
                if (state.selectedNodes.length === 2) state.showConsentModal = true;
            });
        },

        clearActiveEdit: () => {
            set((state) => {
                state.activeEditMode = 'none';
                state.selectedNodes = [];
                state.showConsentModal = false;
            });
        },

    })),
);

/* ------------------- hooks -------------------------------- */

export const useStartLinkCreation = () => networkEditStore((s) => () => s.setActiveEditMode('addLink'));
export const useStartAddPlayer = () => networkEditStore((s) => () => s.setActiveEditMode('addPlayer'));
export const useStartDrag = () => networkEditStore((s) => () => s.setActiveEditMode('dragNode'));

export const useSelectNodeForLink = () => networkEditStore((s) => s.selectNode);
export const useClearActiveEdit = () => networkEditStore((s) => s.clearActiveEdit);

export const useNodePopupLocation = () => networkEditStore((s) => s.nodePopupLocation);
export const useSetNodePopupLocation = () => networkEditStore((s) => s.setNodePopupLocation);

export const useIsLinkCreationActive = () =>
    networkEditStore((s) => s.activeEditMode === 'addLink');

// TODO remove useActiveEditMode and replace with useIsDragActive, useIsAddPlayerActive, useIsLinkCreationActive etc.
export const useActiveEditMode = () => networkEditStore((s) => s.activeEditMode);
export const useSelectedNodes = () => networkEditStore((s) => s.selectedNodes);
export const useShowConsentModal = () => networkEditStore((s) => s.showConsentModal);

export const useIsPopupEnabled = () => {
    const mode = networkEditStore((s) => s.activeEditMode);
    const linkActive = mode === 'addLink';
    return !linkActive && mode !== 'addPlayer' && mode !== 'dragNode';
};
